import { useEffect } from "react";
import { useRouteError } from "react-router-dom";
import { trackFailure } from "@/lib/integrations/sentry-tracking";

/**
 * Root-level error boundary for the RootProviders layout route.
 *
 * Must be dependency-free: if RootProviders itself throws, none of its
 * child providers (I18n, Theme, Auth, etc.) are mounted. This component
 * therefore avoids useTranslation, @speakai/ui, Zustand, and any other
 * context-dependent import. Inline styles prevent reliance on Tailwind CSS
 * variables that may not be available when the provider tree fails.
 */
export function RootError() {
  const error = useRouteError();
  const message = error instanceof Error ? error.message : undefined;
  const errorName = error instanceof Error ? error.name : undefined;
  const errorStack = error instanceof Error && error.stack ? error.stack.slice(0, 500) : undefined;

  useEffect(() => {
    if (!error) return;
    trackFailure(
      "navigation",
      "route_error",
      {
        url: typeof window !== "undefined" ? window.location.pathname : "",
        error_reason: message ?? String(error).slice(0, 200),
        error_name: errorName,
        error_stack: errorStack,
        scope: "root",
      },
      // Shared across every route in the app (this is the outermost boundary);
      // without a per-cause fingerprint, unrelated crashes merge into one issue
      // with a misleading culprit (see SPEAK-CLIENT-7E).
      ["root", errorName ?? "UnknownError"],
    );
  }, [error, message, errorName, errorStack]);

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#0f172a",
        color: "#f8fafc",
        fontFamily: "system-ui, -apple-system, sans-serif",
        padding: "24px",
      }}
    >
      <div style={{ textAlign: "center", maxWidth: "420px" }}>
        {/* Warning triangle — inline SVG, zero external deps */}
        <svg
          aria-hidden="true"
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#f59e0b"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ margin: "0 auto 16px" }}
        >
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>

        <h2 style={{ fontSize: "1.5rem", fontWeight: 600, margin: "0 0 8px" }}>
          Something went wrong
        </h2>
        <p style={{ fontSize: "0.875rem", color: "#94a3b8", margin: "0 0 8px" }}>
          An unexpected error occurred. Please reload the page to try again.
        </p>

        {import.meta.env.DEV && message && (
          <p
            style={{
              fontSize: "0.75rem",
              fontFamily: "monospace",
              backgroundColor: "#1e293b",
              borderRadius: "6px",
              padding: "8px 12px",
              color: "#94a3b8",
              wordBreak: "break-word",
              margin: "0 0 24px",
            }}
          >
            {message}
          </p>
        )}

        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            marginTop: message ? 0 : "24px",
            padding: "10px 20px",
            backgroundColor: "#6366f1",
            color: "#ffffff",
            border: "none",
            borderRadius: "8px",
            fontSize: "0.875rem",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Reload page
        </button>
      </div>
    </div>
  );
}
